import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Ship-screen packing editor. Rolls are draggable chips; boxes are drop zones.
// Dragging recomputes each box's weight live and autosaves the arrangement to
// the shipment, which the server rebuilds into parcels for rating + purchase.
export default class extends Controller {
  static targets = ["boxes", "box", "zone", "badge", "dims", "status"]
  static values = { saveUrl: String, ratesUrl: String, cap: Number, csrf: String }

  connect() {
    this.sortables = []
    this.zoneTargets.forEach(zone => this.makeSortable(zone))
    this.refreshBadges()
    this.refreshDims()
    // Only un-dim when the frame that just loaded is the re-rate for the LATEST
    // edit (editSeq === rerateSeq). If a newer edit arrived mid-flight, its own
    // re-rate will clear the dim — clearing here would re-enable Buy at a price
    // for the wrong (superseded) layout.
    this.onFrameLoad = e => {
      if (e.target && e.target.id === "ship_rates" && this.editSeq === this.rerateSeq) this.clearRatesStale()
    }
    // A carrier/network failure makes the frame "miss" — keep the prices dimmed
    // (never a stale buy) and tell the operator to reload rather than leaving
    // them stuck with a silent "updating…" banner.
    this.onFrameMissing = e => {
      if (!e.target || e.target.id !== "ship_rates") return
      e.preventDefault()
      this.setRatesStatus("Couldn't update prices — reload the page to try again.")
    }
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    document.addEventListener("turbo:frame-missing", this.onFrameMissing)
  }

  disconnect() {
    this.sortables.forEach(s => s.destroy())
    this.sortables = []
    clearTimeout(this.reRateTimer)
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    document.removeEventListener("turbo:frame-missing", this.onFrameMissing)
  }

  makeSortable(zone) {
    this.sortables.push(Sortable.create(zone, {
      group: "packing",
      draggable: ".pe-chip",
      // Use SortableJS's own pointer-based dragging instead of the native
      // HTML5 drag API — the native path is flaky (drags fail to start at the
      // list edges, especially inside a CSS grid) and conflicts with any
      // draggable attribute. forceFallback makes every box behave the same.
      forceFallback: true,
      fallbackTolerance: 3,
      animation: 150,
      ghostClass: "pe-ghost",
      onEnd: () => { this.pruneEmpty(); this.refreshBadges(); this.refreshDims(); this.save() }
    }))
  }

  // After a drag settles, drop any box the move left empty so the operator
  // doesn't stare at phantom "0 rolls" cartons (the server already omits empty
  // boxes on save — this keeps the screen honest). Never removes the last box,
  // and the box a drag just filled is not empty, so it survives.
  pruneEmpty() {
    const empties = this.boxTargets.filter(box => {
      const zone = box.querySelector("[data-packing-editor-target='zone']")
      return zone && !zone.querySelector("[data-sid]")
    })
    // Keep at least one box on screen even if every box ended up empty.
    empties.slice(0, Math.max(0, this.boxTargets.length - 1)).forEach(box => {
      const zone = box.querySelector("[data-packing-editor-target='zone']")
      const i = this.sortables.findIndex(s => s.el === zone)
      if (i !== -1) { this.sortables[i].destroy(); this.sortables.splice(i, 1) }
      box.remove()
    })
    if (empties.length) this.renumber()
  }

  // Clone the first box as an empty drop target so the operator can split
  // rolls into an extra carton. Empty boxes are dropped server-side on save.
  addBox(event) {
    event.preventDefault()
    const first = this.boxTargets[0]
    if (!first) return

    const clone = first.cloneNode(true)
    const zone = clone.querySelector("[data-packing-editor-target='zone']")
    zone.innerHTML = ""
    this.boxesTarget.appendChild(clone)
    this.renumber()
    this.makeSortable(zone)
    this.refreshBadges()
    this.refreshDims()
  }

  renumber() {
    this.boxTargets.forEach((box, i) => {
      const num = box.querySelector("[data-box-num]")
      const label = box.querySelector("[data-box-label]")
      if (num) num.textContent = i + 1
      if (label) label.textContent = `Box ${i + 1}`
    })
  }

  refreshBadges() {
    this.boxTargets.forEach(box => {
      const chips = box.querySelectorAll("[data-weight]")
      const manual = this.manualDims(box)
      const pinned = manual && manual.wt != null
      const total = pinned ? manual.wt : this.derivedWeight(box)
      const badge = box.querySelector("[data-packing-editor-target='badge']")
      if (!badge) return
      badge.textContent = `${total.toFixed(1)} lb${pinned ? " (set)" : ""} · ${chips.length} roll${chips.length === 1 ? "" : "s"}`
      badge.classList.toggle("pe-over", this.hasCapValue && this.capValue > 0 && total > this.capValue)
    })
  }

  // Show each box's OUTER dimensions the way the server derives them at rating
  // time: lay the rolls out in a near-square grid (cols = ceil(√n), rows =
  // ceil(n/cols)), keep the longest roll's length, and scale the max width/height
  // by the grid. Kept in lockstep with CartonizationService#parcel_for so the
  // operator sees the exact box the carrier will rate. "(est.)" flags a box that
  // contains a roll with no real dimensions (fell back to a guessed box).
  refreshDims() {
    this.boxTargets.forEach(box => {
      const el = box.querySelector("[data-packing-editor-target='dims']")
      if (!el || el.dataset.editing === "1") return   // leave an open editor alone

      const manual = this.manualDims(box)
      if (manual) {
        el.innerHTML = `${this.fmt(manual.l)} × ${this.fmt(manual.w)} × ${this.fmt(manual.h)} in ` +
                       `<span class="pe-pin">· set</span>`
        return
      }
      const d = this.derivedDims(box)
      el.textContent = d ? `${this.fmt(d.l)} × ${this.fmt(d.w)} × ${this.fmt(d.h)} in${d.est ? " (est.)" : ""}` : ""
    })
  }

  // The operator's pinned box for a box element, or null when it's auto. Carries
  // outer dims (l/w/h) and, when the operator set it, a manual weight (wt).
  manualDims(box) {
    if (!box.dataset.manualDims) return null
    try {
      const d = JSON.parse(box.dataset.manualDims)
      const pin = { l: +d.l, w: +d.w, h: +d.h }
      if (d.wt != null && d.wt !== "") pin.wt = +d.wt
      return pin
    } catch { return null }
  }

  // Sum of the rolls' weights in a box (the auto weight, before any pin).
  derivedWeight(box) {
    return [...box.querySelectorAll("[data-weight]")]
      .reduce((sum, c) => sum + (parseFloat(c.dataset.weight) || 0), 0)
  }

  // The product-derived outer dims for a box (same grid math as parcel_for), or
  // null for an empty box.
  derivedDims(box) {
    const chips = [...box.querySelectorAll("[data-length]")]
    if (!chips.length) return null
    const cols = Math.ceil(Math.sqrt(chips.length))
    const rows = Math.ceil(chips.length / cols)
    const maxOf = attr => Math.max(...chips.map(c => parseFloat(c.getAttribute(`data-${attr}`)) || 0))
    return { l: maxOf("length"), w: maxOf("width") * cols, h: maxOf("height") * rows,
             est: chips.some(c => c.getAttribute("data-estimated") === "true") }
  }

  // Click the dims line -> inline L/W/H + weight inputs pre-filled with the box's
  // current size and weight.
  editDims(event) {
    const box = event.target.closest("[data-packing-editor-target='box']")
    const el = box?.querySelector("[data-packing-editor-target='dims']")
    if (!el || el.dataset.editing === "1") return   // ignore clicks inside the open editor

    const manual = this.manualDims(box)
    const cur = manual || this.derivedDims(box) || { l: "", w: "", h: "" }
    const curWt = (manual && manual.wt != null) ? manual.wt : this.derivedWeight(box)
    const val = n => (n === "" || n == null ? "" : this.fmt(n))
    el.dataset.editing = "1"
    el.innerHTML =
      ["l", "w", "h"].map((d, i) =>
        `${i ? " × " : ""}<input class="pe-dim-in" data-dim="${d}" type="number" min="0.1" step="0.1" value="${val(cur[d])}">`
      ).join("") +
      ` in · <input class="pe-dim-in" data-dim="wt" type="number" min="0.1" step="0.1" value="${val(curWt)}"> lb ` +
      `<button type="button" class="pe-dim-btn" data-action="packing-editor#saveDims" title="Save">✓</button>` +
      `<button type="button" class="pe-dim-btn" data-action="packing-editor#cancelDims" title="Cancel">✕</button>` +
      (box.dataset.manualDims ? `<button type="button" class="pe-dim-btn" data-action="packing-editor#resetDims" title="Back to auto">auto</button>` : "")
    el.querySelector("[data-dim='l']")?.focus()
  }

  saveDims(event) {
    const box = event.target.closest("[data-packing-editor-target='box']")
    const el = box.querySelector("[data-packing-editor-target='dims']")
    const read = d => parseFloat(el.querySelector(`[data-dim='${d}']`)?.value)
    const pin = { l: read("l"), w: read("w"), h: read("h"), wt: read("wt") }
    if (![pin.l, pin.w, pin.h, pin.wt].every(n => n > 0)) { el.querySelector("[data-dim='l']")?.focus(); return }

    box.dataset.manualDims = JSON.stringify(pin)
    delete el.dataset.editing
    this.refreshDims()
    this.refreshBadges()   // the pinned weight now drives the badge
    this.save()
  }

  cancelDims(event) {
    const el = event.target.closest("[data-packing-editor-target='box']")
                    .querySelector("[data-packing-editor-target='dims']")
    delete el.dataset.editing
    this.refreshDims()
  }

  resetDims(event) {
    const box = event.target.closest("[data-packing-editor-target='box']")
    delete box.dataset.manualDims
    delete box.querySelector("[data-packing-editor-target='dims']").dataset.editing
    this.refreshDims()
    this.refreshBadges()   // weight reverts to the rolls' sum
    this.save()
  }

  // Round to 0.1 in and drop a trailing ".0" so "20.0" reads as "20".
  fmt(v) {
    const r = Math.round(v * 10) / 10
    return Number.isInteger(r) ? String(r) : r.toFixed(1)
  }

  // Boxes that actually hold rolls, in display order (empty boxes omitted).
  filledBoxes() {
    return this.boxTargets.filter(box => box.querySelector("[data-sid]"))
  }

  // The layout shape the controller expects: [ { <shipment_item_id>: qty }, ... ].
  serialize() {
    return this.filledBoxes().map(box => {
      const counts = {}
      box.querySelectorAll("[data-sid]").forEach(chip => {
        counts[chip.dataset.sid] = (counts[chip.dataset.sid] || 0) + 1
      })
      return counts
    })
  }

  // Per-box manual dimension pins, aligned 1:1 with serialize() (null = auto).
  serializeBoxDims() {
    return this.filledBoxes().map(box => this.manualDims(box))
  }

  save() {
    this.setStatus("Saving…")
    // Cancel any re-rate queued by an earlier, now-superseded edit so the only
    // re-rate that fires is the one armed by THIS (latest) edit's saved layout —
    // otherwise a stale timer could re-enable Buy against the wrong boxes.
    clearTimeout(this.reRateTimer)
    // Immediately mark the shown prices as stale so a stale rate can't be bought
    // in the window between the edit and the re-rate landing.
    this.markRatesStale()
    fetch(this.saveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": this.csrfValue,
        "Accept": "application/json"
      },
      body: new URLSearchParams({
        layout: JSON.stringify(this.serialize()),
        box_dims: JSON.stringify(this.serializeBoxDims())
      })
    })
      .then(res => {
        if (res.ok) { this.setStatus("Saved"); this.scheduleReRate() }
        else this.setStatus("Couldn't save — refresh and retry")
      })
      .catch(() => this.setStatus("Couldn't save — check your connection"))
  }

  // --- Re-rate the carrier prices after an edit -------------------------------
  // The rate rows live in a turbo-frame (#ship_rates). An edit dims that frame
  // (Buy unclickable) and, once the layout has persisted, reloads it — the GET
  // re-rates the SAVED boxes so the price on the Buy button always equals what
  // the carrier will charge. If the reload fails, the frame stays dimmed (Buy
  // stays disabled) — fail safe, never a stale purchase.
  get ratesFrame() { return document.getElementById("ship_rates") }

  markRatesStale() {
    this.editSeq = (this.editSeq || 0) + 1   // bumps every edit; guards the re-rate/clear
    const frame = this.ratesFrame
    if (frame) { frame.classList.add("pe-stale"); frame.inert = true }   // inert: no click OR keyboard submit
    this.setRatesStatus("Boxes changed — updating prices…")
  }

  clearRatesStale() {
    const frame = this.ratesFrame
    if (frame) { frame.classList.remove("pe-stale"); frame.inert = false }
    const status = document.getElementById("ship_rates_status")
    if (status) status.hidden = true
  }

  setRatesStatus(text) {
    const status = document.getElementById("ship_rates_status")
    if (status) { status.textContent = text; status.hidden = false }
  }

  scheduleReRate() {
    clearTimeout(this.reRateTimer)
    this.reRateTimer = setTimeout(() => this.reRate(), 900)
  }

  reRate() {
    const frame = this.ratesFrame
    if (!frame || !this.hasRatesUrlValue) return
    this.rerateSeq = this.editSeq   // this re-rate reflects the layout of edit #editSeq
    // First refresh sets src (Turbo fetches it); later refreshes reload it.
    if (frame.getAttribute("src")) frame.reload()
    else frame.setAttribute("src", this.ratesUrlValue)
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
